import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
}

const ProtectedRoute = ({ children, requireOnboarding = true }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checkingProfile, setCheckingProfile] = useState(requireOnboarding);
  const [onboardingDone, setOnboardingDone] = useState<boolean | null>(null);
  const [inboxPromptSeen, setInboxPromptSeen] = useState<boolean | null>(null);

  useEffect(() => {
    if (!requireOnboarding || !user) return;

    (async () => {
      try {
        const { data } = await supabase
          .from("profiles")
          .select("onboarding_completed, inbox_prompt_seen")
          .eq("user_id", user.id)
          .maybeSingle();
        setOnboardingDone(data?.onboarding_completed ?? false);
        setInboxPromptSeen((data as any)?.inbox_prompt_seen ?? false);
      } catch {
        // Allow through on network failure rather than infinite loading
      } finally {
        setCheckingProfile(false);
      }
    })();
  }, [user, requireOnboarding]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (checkingProfile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (requireOnboarding && onboardingDone === false) {
    return <Navigate to="/onboarding" replace />;
  }

  // Redirect to inbox check if onboarding is done but prompt not yet seen
  if (
    requireOnboarding &&
    onboardingDone === true &&
    inboxPromptSeen === false &&
    location.pathname !== "/inbox-check"
  ) {
    return <Navigate to="/inbox-check" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
